/**
 * 본부 게이트 관리 (대표 전용)
 *
 * 하는 일 두 가지:
 *  ① 매장별 게이트 현황 — 열쇠 발급, 지금 서버에 붙어 있는지, 어느 펌웨어인지
 *  ② 펌웨어 무선 배포 — .bin 을 올려두면 각 매장 게이트가 스스로 받아 설치한다
 *
 * 🔴 게이트는 매장 내부망이라 서버가 먼저 말을 걸 수 없다. 게이트가 서버로 폴링해 온다.
 *    그래서 "붙어 있음"은 최근 90초 안에 소식을 들었다는 뜻이다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import hqGateService, { GateFirmware, StoreGate } from '../services/hqGateService';

const HqGatesPage: React.FC = () => {
  const [stores, setStores] = useState<StoreGate[]>([]);
  const [firmwares, setFirmwares] = useState<GateFirmware[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [shownToken, setShownToken] = useState<{ org: string; token: string } | null>(null);

  // 업로드 폼
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [activateNow, setActivateNow] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([
        hqGateService.listStores(),
        hqGateService.listFirmwares().catch(() => [] as GateFirmware[]),
      ]);
      setStores(s);
      setFirmwares(f);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || '불러오지 못했습니다');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // 게이트 접속 상태는 계속 변하므로 주기적으로 갱신
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const flash = (text: string, isError = false) => {
    if (isError) { setErr(text); setMsg(''); } else { setMsg(text); setErr(''); }
    setTimeout(() => { setMsg(''); setErr(''); }, 5000);
  };

  const handleToken = async (org: StoreGate, rotate: boolean) => {
    if (rotate && !window.confirm(
      `${org.organization_name}\n\n열쇠를 새로 발급하면 지금 게이트는 서버와 끊깁니다.\n` +
      '게이트에 새 열쇠를 다시 넣어야 원격 개방·펌웨어 갱신이 다시 됩니다.\n\n계속할까요?'
    )) return;
    setBusy(org.organization_id);
    try {
      const r = await hqGateService.issueToken(org.organization_id, rotate);
      setShownToken({ org: r.organization_name, token: r.gate_token });
      await load();
    } catch (e: any) {
      flash(e?.response?.data?.detail || '열쇠 발급 실패', true);
    } finally { setBusy(null); }
  };

  const handleOpen = async (org: StoreGate, direction: 'entry' | 'exit') => {
    setBusy(org.organization_id);
    try {
      await hqGateService.openGate(org.organization_id, direction);
      flash(`🚪 ${org.organization_name} ${direction === 'entry' ? '입구' : '출구'}를 열었습니다`);
    } catch (e: any) {
      flash(e?.response?.data?.detail || '열지 못했습니다', true);
    } finally { setBusy(null); }
  };

  const handleUpload = async () => {
    if (!file) return flash('펌웨어 파일(.bin)을 골라주세요', true);
    if (!version.trim()) return flash('버전을 적어주세요 (펌웨어의 FIRMWARE_VERSION 과 같아야 합니다)', true);
    if (activateNow && !window.confirm(
      `v${version} 을 전 매장에 배포합니다.\n\n` +
      '각 매장 게이트가 스스로 받아 설치하고 재부팅합니다.\n' +
      '문이 막 열린 직후에는 설치를 미루므로 이용 중 갑자기 닫히지는 않습니다.\n\n계속할까요?'
    )) return;
    setUploading(true);
    try {
      await hqGateService.uploadFirmware(file, version.trim(), notes.trim(), activateNow);
      flash(activateNow ? `v${version} 업로드 + 배포 시작` : `v${version} 업로드 완료 (아직 배포 안 함)`);
      setFile(null); setVersion(''); setNotes(''); setActivateNow(false);
      await load();
    } catch (e: any) {
      flash(e?.response?.data?.detail || '업로드 실패', true);
    } finally { setUploading(false); }
  };

  const handleActivate = async (fw: GateFirmware) => {
    if (!window.confirm(`v${fw.version} 을 전 매장에 배포할까요?`)) return;
    try {
      await hqGateService.activate(fw.id);
      flash(`v${fw.version} 배포 시작`);
      await load();
    } catch (e: any) { flash(e?.response?.data?.detail || '배포 실패', true); }
  };

  const handleStop = async () => {
    try {
      const r = await hqGateService.stopRollout();
      flash(`배포 중단 (${r.deactivated}건). 이미 받은 게이트는 그대로입니다`);
      await load();
    } catch (e: any) { flash(e?.response?.data?.detail || '중단 실패', true); }
  };

  // 왜 새 기능이 안 되는지 한눈에 — 대표가 매장에 뭘 요청해야 할지 바로 알 수 있게
  const STATUS: Record<string, { label: string; cls: string; hint: string }> = {
    ready:        { label: '사용 가능',   cls: 'bg-green-100 text-green-800',
                    hint: '원격 개방·무선 갱신이 됩니다' },
    update_ready: { label: '갱신 대기',   cls: 'bg-blue-100 text-blue-800',
                    hint: '배포 중인 펌웨어와 버전이 달라 곧 스스로 갱신됩니다' },
    offline:      { label: '연결 끊김',   cls: 'bg-amber-100 text-amber-800',
                    hint: '전에는 붙었습니다. 게이트 전원과 인터넷을 확인해주세요' },
    legacy:       { label: '옛 펌웨어',   cls: 'bg-gray-200 text-gray-700',
                    hint: '서버에 붙은 적이 없습니다. 펌웨어를 v1.8 이상으로 올려야 원격 개방·무선 갱신이 됩니다' },
    no_gate:      { label: '게이트 없음', cls: 'bg-gray-100 text-gray-500',
                    hint: '이 매장에는 게이트가 등록돼 있지 않습니다' },
  };

  const fmtDate = (iso?: string | null) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', dateStyle: 'short', timeStyle: 'short' });
    } catch { return null; }
  };

  const counts = stores.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const fmtSize = (n: number) => `${(n / 1024 / 1024).toFixed(2)} MB`;
  const fmtAgo = (s?: number | null) =>
    s == null ? '-' : s < 60 ? `${s}초 전` : s < 3600 ? `${Math.floor(s / 60)}분 전` : `${Math.floor(s / 3600)}시간 전`;

  if (loading) return <div className="p-6 text-gray-500">불러오는 중...</div>;

  return (
    <div className="p-6 max-w-6xl">
      <h1 className="text-2xl font-bold mb-1">🚪 게이트 관리</h1>
      <p className="text-sm text-gray-500 mb-6">
        매장 게이트의 열쇠를 발급하고, 펌웨어를 무선으로 배포합니다.
      </p>

      {msg && <div className="mb-4 px-4 py-3 rounded bg-green-50 text-green-800 text-sm">{msg}</div>}
      {err && <div className="mb-4 px-4 py-3 rounded bg-red-50 text-red-700 text-sm">{err}</div>}

      {shownToken && (
        <div className="mb-6 p-4 rounded-lg border border-amber-300 bg-amber-50">
          <div className="font-semibold text-amber-900 mb-1">{shownToken.org} 게이트 열쇠</div>
          <code className="block p-2 bg-white rounded border text-sm break-all">{shownToken.token}</code>
          <p className="text-xs text-amber-800 mt-2">
            이 값을 게이트에 넣어야 원격 개방·펌웨어 갱신이 됩니다.
            노트북을 USB 로 연결해 시리얼(115200)에 아래 한 줄을 붙여넣으세요.
          </p>
          <code className="block p-2 mt-2 bg-white rounded border text-xs break-all">
            {`{"store":"${shownToken.org}","token":"${shownToken.token}","api_base":"https://api.newattend.com"}`}
          </code>
          <button className="mt-3 text-sm text-amber-900 underline" onClick={() => setShownToken(null)}>닫기</button>
        </div>
      )}

      {/* ① 매장별 현황 */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-3">매장별 게이트</h2>

      <div className="mb-3 flex flex-wrap gap-2 text-xs">
        {(['ready', 'update_ready', 'offline', 'legacy', 'no_gate'] as const).map(k => (
          counts[k] ? (
            <span key={k} className={`px-2 py-1 rounded ${STATUS[k].cls}`}>
              {STATUS[k].label} {counts[k]}곳
            </span>
          ) : null
        ))}
        {(counts.legacy || counts.no_gate) ? (
          <span className="px-2 py-1 text-gray-500">
            ← 이 매장들은 아직 원격 개방·무선 갱신을 쓸 수 없습니다
          </span>
        ) : null}
      </div>
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-2">매장</th>
                <th className="text-left px-4 py-2">상태</th>
                <th className="text-left px-4 py-2">열쇠</th>
                <th className="text-left px-4 py-2">서버 연결</th>
                <th className="text-left px-4 py-2">펌웨어</th>
                <th className="text-left px-4 py-2">게이트 IP</th>
                <th className="text-right px-4 py-2">작업</th>
              </tr>
            </thead>
            <tbody>
              {stores.map(s => (
                <tr key={s.organization_id} className="border-t">
                  <td className="px-4 py-2 font-medium">{s.organization_name}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${(STATUS[s.status] || STATUS.no_gate).cls}`}
                          title={(STATUS[s.status] || STATUS.no_gate).hint}>
                      {(STATUS[s.status] || STATUS.no_gate).label}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {s.has_token
                      ? <span className="text-green-700">발급됨</span>
                      : <span className="text-gray-400">없음</span>}
                  </td>
                  <td className="px-4 py-2">
                    {s.gate_linked
                      ? <span className="text-green-700">● 연결됨</span>
                      : (
                        <span className="text-gray-400">
                          ○ 끊김
                          <span className="block text-xs">
                            {fmtDate(s.last_seen_at) ? `마지막 ${fmtDate(s.last_seen_at)}` : '기록 없음'}
                          </span>
                        </span>
                      )}
                  </td>
                  <td className="px-4 py-2 text-gray-600">
                    {s.gate_version || '-'}
                    {s.status === 'update_ready' && s.active_version && (
                      <span className="block text-xs text-blue-700">→ {s.active_version} 예정</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-500 text-xs">{s.esp32_entry_ip || '-'}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      disabled={busy === s.organization_id}
                      onClick={() => handleToken(s, s.has_token)}
                      className="px-2 py-1 text-xs rounded border hover:bg-gray-50 mr-1"
                    >
                      {s.has_token ? '열쇠 보기/재발급' : '열쇠 발급'}
                    </button>
                    <button
                      disabled={busy === s.organization_id || !s.gate_linked}
                      onClick={() => handleOpen(s, 'entry')}
                      title={s.gate_linked ? '입구를 엽니다' : '게이트가 서버에 붙어 있지 않습니다'}
                      className="px-2 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-40 mr-1"
                    >
                      입구 열기
                    </button>
                    <button
                      disabled={busy === s.organization_id || !s.gate_linked}
                      onClick={() => handleOpen(s, 'exit')}
                      title={s.gate_linked ? '출구를 엽니다 (입출구 통합형이면 같은 문이 열립니다)' : '게이트가 서버에 붙어 있지 않습니다'}
                      className="px-2 py-1 text-xs rounded bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-40"
                    >
                      출구 열기
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          ‘연결됨’은 최근 90초 안에 게이트가 서버로 소식을 보내왔다는 뜻입니다.
          옛 펌웨어(v1.7 이하)는 서버에 붙지 않으므로 항상 ‘끊김’으로 보입니다.
          <br />
          입구·출구가 나뉜 투웨이 게이트(KC868)는 각각 열리고,
          문이 하나인 자동문(C6)은 어느 쪽을 눌러도 그 문이 열립니다.
        </p>
      </section>

      {/* ② 펌웨어 배포 */}
      <section>
        <h2 className="text-lg font-semibold mb-3">펌웨어 무선 배포</h2>

        <div className="p-4 border rounded-lg bg-gray-50 mb-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">펌웨어 파일 (.bin)</span>
              <input type="file" accept=".bin"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm" />
            </label>
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">버전 (펌웨어의 FIRMWARE_VERSION 과 동일)</span>
              <input value={version} onChange={e => setVersion(e.target.value)}
                placeholder="1.8.1" className="w-full px-3 py-2 border rounded" />
            </label>
          </div>
          <label className="block text-sm mt-3">
            <span className="block text-gray-600 mb-1">메모 (무엇이 바뀌었는지)</span>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full px-3 py-2 border rounded" />
          </label>
          <label className="flex items-center gap-2 mt-3 text-sm">
            <input type="checkbox" checked={activateNow} onChange={e => setActivateNow(e.target.checked)} />
            <span>올리자마자 전 매장에 배포</span>
          </label>
          <button onClick={handleUpload} disabled={uploading}
            className="mt-4 px-4 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50">
            {uploading ? '올리는 중...' : '업로드'}
          </button>
        </div>

        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-2">버전</th>
                <th className="text-left px-4 py-2">메모</th>
                <th className="text-left px-4 py-2">크기</th>
                <th className="text-left px-4 py-2">올린 사람</th>
                <th className="text-left px-4 py-2">상태</th>
                <th className="text-right px-4 py-2">작업</th>
              </tr>
            </thead>
            <tbody>
              {firmwares.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">올린 펌웨어가 없습니다</td></tr>
              )}
              {firmwares.map(f => (
                <tr key={f.id} className="border-t">
                  <td className="px-4 py-2 font-medium">v{f.version}</td>
                  <td className="px-4 py-2 text-gray-600">{f.notes || '-'}</td>
                  <td className="px-4 py-2 text-gray-500">{fmtSize(f.size_bytes)}</td>
                  <td className="px-4 py-2 text-gray-500">{f.uploaded_by || '-'}</td>
                  <td className="px-4 py-2">
                    {f.is_active
                      ? <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs">배포 중</span>
                      : <span className="text-gray-400 text-xs">대기</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {f.is_active
                      ? <button onClick={handleStop}
                          className="px-2 py-1 text-xs rounded border hover:bg-gray-50">배포 중단</button>
                      : <button onClick={() => handleActivate(f)}
                          className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700">배포</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          배포하면 각 매장 게이트가 자기 버전과 다를 때만 내려받아 설치합니다.
          설치에 실패하면 기존 펌웨어로 계속 동작하고, 되돌리려면 이전 버전을 다시 [배포]하면 됩니다.
        </p>
      </section>
    </div>
  );
};

export default HqGatesPage;
